import { motion } from 'framer-motion';
import Layout from '@/components/Layout';
import { STATS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { MapPin, Users, BookOpen, Award, Star, ChevronRight } from 'lucide-react';

const timelineEvents = [
  {
    year: '1904',
    title: '肇始创办',
    description: '严修先生创办严氏家馆，这是南开系列学校的最初起点，播下了南开教育的第一粒种子。',
    highlight: false,
  },
  {
    year: '1919',
    title: '南开大学成立',
    description: '张伯苓与严修正式创办南开大学，成为中国第一所私立大学之一，秉承「允公允能、日新月异」的校训。',
    highlight: true,
  },
  {
    year: '1937',
    title: '抗日战争',
    description: '抗战爆发，日军炸毁南开校园。南开大学与北京大学、清华大学联合组建西南联合大学，弦歌不辍，薪火相传。',
    highlight: false,
  },
  {
    year: '1946',
    title: '复员复校',
    description: '抗战胜利后，南开大学回迁天津，在废墟上重建家园，重振学校，恢复并发展各学科建设。',
    highlight: false,
  },
  {
    year: '1952',
    title: '院系调整',
    description: '全国高等院校院系调整，南开大学由综合性大学调整为以理科和社会科学为主的综合大学，成为国家重点高校。',
    highlight: false,
  },
  {
    year: '1978',
    title: '改革开放',
    description: '改革开放后，南开大学迎来历史性发展机遇，综合实力大幅提升，跻身全国顶尖高校行列。',
    highlight: false,
  },
  {
    year: '2017',
    title: '双一流建设',
    description: '南开大学入选国家「双一流」建设A类高校，化学、数学、统计学等多个学科列入世界一流学科建设名单。',
    highlight: true,
  },
];

const leadershipData = [
  {
    name: '曹雪涛',
    title: '中国工程院院士、南开大学校长',
    description: '著名免疫学家，长期致力于固有免疫应答机制及炎症调控研究，在国内外享有重要学术声誉。',
    avatar: '曹',
  },
  {
    name: '杨庆山',
    title: '南开大学党委书记',
    description: '长期从事高等教育管理工作，致力于推动南开大学高质量发展，深化改革，落实立德树人根本任务。',
    avatar: '杨',
  },
];

const campusData = [
  {
    name: '八里台校区',
    description: '南开大学本部校区，位于天津市南开区，是学校的核心校区，拥有众多历史建筑与现代教学楼群，环境优美。',
    area: '约1.36平方公里',
    icon: MapPin,
    features: ['历史建筑群', '中心图书馆', '学生生活区', '各主要学院'],
  },
  {
    name: '津南校区',
    description: '位于天津市津南区海河教育园区，是南开大学新建的现代化校区，设施完善，环境开阔，承担大量本科教学任务。',
    area: '约2.46平方公里',
    icon: MapPin,
    features: ['现代教学楼', '综合体育中心', '国际学术交流中心', '创新创业园'],
  },
  {
    name: '泰达校区',
    description: '位于天津经济技术开发区，依托开发区的产业优势，重点发展应用型学科和产学研合作，服务区域经济发展。',
    area: '约0.25平方公里',
    icon: MapPin,
    features: ['工程技术学院', '产学研基地', '企业合作中心', '实验实训中心'],
  },
];

const keyFacts = [
  { label: '国家重点实验室', value: '4', unit: '个' },
  { label: '院士', value: '60+', unit: '位' },
  { label: '双一流学科', value: '6', unit: '个' },
  { label: '国家级科研奖励', value: '200+', unit: '项' },
  { label: '国际合作院校', value: '280', unit: '所' },
  { label: '在校学生', value: '35,546', unit: '人' },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function About() {
  return (
    <Layout>
      <section className="relative w-full h-[480px] overflow-hidden">
        <img
          src={IMAGES.CAMPUS_1}
          alt="南开大学校园"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sidebar/70 via-sidebar/50 to-sidebar/80" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-chart-1/20 text-chart-1 text-sm font-medium mb-4 border border-chart-1/30">
              百年名校 · 南开大学
            </span>
            <h1
              className="text-5xl md:text-6xl font-bold text-white mb-4"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              学校概况
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              创立于1919年，百余年薪火相传，秉承「允公允能、日新月异」校训精神，培育英才无数
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {keyFacts.map((fact) => (
              <motion.div
                key={fact.label}
                variants={fadeInUp}
                className="bg-card border border-border rounded-xl p-5 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div
                  className="text-3xl font-bold text-chart-1 mb-1"
                  style={{ fontFamily: "'Source Code Pro', monospace" }}
                >
                  {fact.value}
                  <span className="text-base ml-0.5">{fact.unit}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{fact.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="text-center mb-14"
          >
            <span className="text-primary text-sm font-medium uppercase tracking-widest">HISTORY</span>
            <h2
              className="text-4xl font-bold text-foreground mt-2"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              历史沿革
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              百余年风雨兼程，南开大学在历史的长河中不断砥砺前行，铸就辉煌
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2 hidden md:block" />

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              className="space-y-8"
            >
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={event.year}
                  variants={fadeInUp}
                  className={`relative flex flex-col md:flex-row gap-6 items-center md:items-start ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div
                    className={`flex-1 ${
                      index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'
                    }`}
                  >
                    <div
                      className={`bg-card border rounded-xl p-6 shadow-sm ${
                        event.highlight
                          ? 'border-primary/40 shadow-primary/10 shadow-md'
                          : 'border-border'
                      }`}
                    >
                      <div
                        className="text-chart-1 text-2xl font-bold mb-1"
                        style={{ fontFamily: "'Source Code Pro', monospace" }}
                      >
                        {event.year}
                      </div>
                      <h3
                        className="text-lg font-semibold text-foreground mb-2"
                        style={{ fontFamily: "'Noto Serif SC', serif" }}
                      >
                        {event.title}
                        {event.highlight && (
                          <span className="ml-2 inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                            重要节点
                          </span>
                        )}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{event.description}</p>
                    </div>
                  </div>

                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-primary bg-background items-center justify-center z-10">
                    <div
                      className={`w-2.5 h-2.5 rounded-full ${
                        event.highlight ? 'bg-chart-1' : 'bg-primary'
                      }`}
                    />
                  </div>

                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="text-center mb-14"
          >
            <span className="text-primary text-sm font-medium uppercase tracking-widest">LEADERSHIP</span>
            <h2
              className="text-4xl font-bold text-foreground mt-2"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              现任领导
            </h2>
            <p className="text-muted-foreground mt-3">引领南开大学迈向更高质量发展的核心团队</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto"
          >
            {leadershipData.map((leader) => (
              <motion.div
                key={leader.name}
                variants={fadeInUp}
                className="bg-card border border-border rounded-2xl p-8 flex items-start gap-6 shadow-sm hover:shadow-md transition-all hover:border-primary/30"
              >
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold flex-shrink-0">
                  {leader.avatar}
                </div>
                <div>
                  <h3
                    className="text-xl font-bold text-foreground mb-1"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    {leader.name}
                  </h3>
                  <p className="text-primary text-sm font-medium mb-3">{leader.title}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{leader.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-sidebar">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="text-center mb-14"
          >
            <span className="text-chart-1 text-sm font-medium uppercase tracking-widest">MOTTO & SPIRIT</span>
            <h2
              className="text-4xl font-bold text-white mt-2"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              校训与精神
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center py-12 px-8 bg-sidebar-accent rounded-3xl border border-sidebar-border mb-12"
          >
            <p className="text-sidebar-foreground/60 text-sm mb-4 tracking-widest uppercase">NANKAI UNIVERSITY MOTTO</p>
            <h3
              className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-wide"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              允公允能
            </h3>
            <div className="w-24 h-px bg-chart-1 mx-auto mb-6" />
            <h3
              className="text-5xl md:text-6xl font-bold text-chart-1 mb-8 leading-tight tracking-wide"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              日新月异
            </h3>
            <p className="text-sidebar-foreground/70 text-base max-w-2xl mx-auto leading-relaxed">
              「允公」，即大公，爱国爱群之公德；「允能」，即大能，服务社会之能力。
              「日新月异」，即与时俱进，不断开拓创新，追求卓越。校训由张伯苓先生于建校初期提出，
              历经百年仍是南开学子的精神座标。
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {[
              {
                icon: Star,
                title: '爱国主义精神',
                desc: '南开大学始终将爱国主义教育放在首位，培养学生对国家、民族的深厚情感与责任担当。',
              },
              {
                icon: BookOpen,
                title: '求真学问精神',
                desc: '秉承严谨治学、追求真理的学术精神，鼓励学生勇于探索、大胆创新，在各学科领域追求卓越。',
              },
              {
                icon: Users,
                title: '奉献社会精神',
                desc: '南开大学培养的人才，始终胸怀家国，投身社会建设，以个人才华和奉献精神服务于国家和人民。',
              },
            ].map((item) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                className="bg-sidebar-accent border border-sidebar-border rounded-xl p-6"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-sidebar-primary" />
                </div>
                <h4 className="text-white font-semibold text-lg mb-2">{item.title}</h4>
                <p className="text-sidebar-foreground/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="text-center mb-14"
          >
            <span className="text-primary text-sm font-medium uppercase tracking-widest">CAMPUS</span>
            <h2
              className="text-4xl font-bold text-foreground mt-2"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              校园概览
            </h2>
            <p className="text-muted-foreground mt-3">三个校区，遍布天津，各具特色，共铸南开</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {campusData.map((campus, index) => (
              <motion.div
                key={campus.name}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-primary/30 transition-all group"
              >
                <div className="h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <campus.icon className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <div className="p-6">
                  <h3
                    className="text-xl font-bold text-foreground mb-1"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    {campus.name}
                  </h3>
                  <p className="text-chart-1 text-sm font-medium mb-3">
                    占地面积 {campus.area}
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{campus.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {campus.features.map((f) => (
                      <span
                        key={f}
                        className="px-2.5 py-1 bg-accent text-accent-foreground rounded-full text-xs"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl overflow-hidden"
          >
            <img
              src={IMAGES.CAMPUS_6}
              alt="南开大学校园风光"
              className="w-full h-72 object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="text-center mb-14"
          >
            <span className="text-primary text-sm font-medium uppercase tracking-widest">ALUMNI</span>
            <h2
              className="text-4xl font-bold text-foreground mt-2"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              杰出校友
            </h2>
            <p className="text-muted-foreground mt-3">南开学子以家国情怀，书写时代华章</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="bg-card border border-border rounded-3xl overflow-hidden shadow-md mb-10"
          >
            <div className="grid md:grid-cols-2">
              <div className="relative">
                <img
                  src={IMAGES.GRADUATION_1}
                  alt="毕业典礼"
                  className="w-full h-full object-cover min-h-[320px]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/20" />
              </div>
              <div className="p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-chart-1" />
                  <span className="text-chart-1 text-sm font-medium">最杰出校友</span>
                </div>
                <h3
                  className="text-4xl font-bold text-foreground mb-2"
                  style={{ fontFamily: "'Noto Serif SC', serif" }}
                >
                  周恩来
                </h3>
                <p className="text-muted-foreground text-sm mb-4">1898 — 1976 · 南开大学1917届校友</p>
                <p className="text-foreground/80 leading-relaxed mb-6">
                  中国人民的伟大领袖、中华人民共和国开国元勋、新中国第一任国务院总理。
                  1913年进入南开学校就读，1917年毕业。
                  周恩来同志在南开求学期间，积极参与爱国学生运动，展现了卓越的领导才能，
                  是南开精神「允公允能」的杰出践行者，被誉为南开最伟大的校友。
                </p>
                <div className="flex flex-wrap gap-3">
                  {['中华人民共和国国务院总理', '开国元勋', '杰出外交家'].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { name: '陈省身', field: '数学家', desc: '国际著名数学家，微分几何之父，南开数学研究所创始人' },
              { name: '曹禺', field: '剧作家', desc: '中国现代戏剧奠基人，《雷雨》等作品影响深远' },
              { name: '吴大猷', field: '物理学家', desc: '中国理论物理学奠基人之一，"中国物理学之父"' },
              { name: '温家宝', field: '政治家', desc: '中华人民共和国前国务院总理，南开大学地质学专业校友' },
            ].map((alumni) => (
              <motion.div
                key={alumni.name}
                variants={fadeInUp}
                className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 hover:shadow-sm transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-primary font-bold text-lg mb-3">
                  {alumni.name[0]}
                </div>
                <h4
                  className="font-bold text-foreground text-base mb-0.5"
                  style={{ fontFamily: "'Noto Serif SC', serif" }}
                >
                  {alumni.name}
                </h4>
                <p className="text-primary text-xs font-medium mb-2">{alumni.field}</p>
                <p className="text-muted-foreground text-xs leading-relaxed">{alumni.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-primary/10 to-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl font-bold text-foreground mb-3"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              学校主要数据
            </h2>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {STATS.map((stat) => (
              <motion.div
                key={stat.id}
                variants={fadeInUp}
                className="bg-card border border-border rounded-xl p-5 text-center shadow-sm"
              >
                <div
                  className="text-2xl font-bold text-chart-1 mb-1"
                  style={{ fontFamily: "'Source Code Pro', monospace" }}
                >
                  {stat.value.toLocaleString()}
                  <span className="text-sm ml-0.5 text-muted-foreground font-normal">{stat.unit}</span>
                </div>
                <div className="text-sm font-medium text-foreground mb-1">{stat.label}</div>
                <div className="text-xs text-muted-foreground">{stat.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-sidebar">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              加入南开大学
            </h2>
            <p className="text-sidebar-foreground/70 mb-8 max-w-xl mx-auto">
              百余年来，南开大学始终以家国情怀为引领，培育德才兼备的时代英才。
              欢迎报考南开大学，共同书写属于您的南开故事。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#admissions"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                了解招生信息
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href="#campus"
                className="inline-flex items-center gap-2 px-8 py-3 bg-sidebar-accent text-sidebar-foreground rounded-lg font-medium hover:bg-sidebar-accent/80 transition-colors border border-sidebar-border"
              >
                参观校园
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
